import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';
import { startSyncEngine, syncWorker } from '../lib/sync/bootstrap';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

/**
 * OfflineContext — network state machine + sync engine lifecycle owner.
 *
 * The engine itself (SyncWorker) also listens to browser online/offline
 * events; this context additionally:
 *  - boots the engine once the app mounts,
 *  - triggers reconciliation on online transitions (belt & braces with the
 *    worker's own listener — both are idempotent),
 *  - performs health-check probing beyond navigator.onLine, because
 *    `navigator.onLine === true` does NOT mean PocketBase is reachable
 *    (captive portals, VPN-only LANs).
 */
export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [serverReachable, setServerReachable] = useState(true);
  const wasOfflineRef = useRef(false);

  // ---- Boot the sync engine for this app session ----------------------
  useEffect(() => {
    startSyncEngine().catch((error) => {
      // Offline boot must never crash the app — Dexie remains the primary
      // datastore and the engine will retry on the next online transition.
      console.warn('Sync engine deferred:', error?.message);
    });
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOfflineRef.current) {
        setWasOffline(true);
        // Reconcile immediately on reconnect; SyncWorker also self-triggers
        // with a short delay, so this is a fast-path duplicate (idempotent).
        void syncWorker.reconcileNow('online-context');
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const value = {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    serverReachable
  };

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
};

/**
 * Hook for components that need a "can I reach the cloud right now?"
 * answer that is more truthful than navigator.onLine.
 */
export const useServerReachability = () => {
  const { serverReachable, isOnline } = useOffline();
  const [reachable, setReachable] = useState(serverReachable);
  const check = useCallback(async () => {
    try {
      const ok = await syncWorker.checkHealth();
      setReachable(ok);
      return ok;
    } catch {
      setReachable(false);
      return false;
    }
  }, []);
  return { serverReachable: reachable, isOnline, check };
};
